import { isInteger, isString, isUndefined } from 'lodash';
import { yupToFormErrors } from 'formik';
import generalUtils from '~/utils/general-utils';
import dateUtils from '~/utils/date-utils';
import { ApiParamError } from './errors';
import { AssignmentStatus } from '../types';
import type { DeliveryTaskProps, PickupTaskProps } from '../types';
import type {
    GetApiParamErrorParams,
    ApiParamGenericValidator,
    ApiParamDetailedValidator,
    ApiParamValidatorReturn,
    ApiParamValidatorMap,
    ApiParamErrorMap,
    AssignmentStatusParamValidationCallback,
    NumberParamValidationCallback,
    StringParamValidationCallback,
    TaskDataParamValidationCallback
} from './types';
import type { AnySchema } from 'yup';
import { ValidationError } from 'yup';

/**
 * A generic function to retrieve a specific parameter error for a given error
 */
const getParamErrors = ({
    isMissing,
    isInvalid,
    missingMessage,
    invalidMessage,
    errorMessage
}: GetApiParamErrorParams) => {
    if (isMissing) {
        return (
            missingMessage ||
            errorMessage ||
            ApiParamError.GENERIC_MISSING_PARAM
        );
    }

    if (isInvalid) {
        return (
            invalidMessage ||
            errorMessage ||
            ApiParamError.GENERIC_INVALID_PARAM
        );
    }

    return undefined;
};

/**
 * Standardize test results from validator functions
 */
const getErrorTestResults = (
    testResults: ApiParamValidatorReturn | boolean
): ApiParamValidatorReturn => {
    if (typeof testResults === 'boolean')
        return { isMissing: false, isInvalid: !testResults };

    return testResults;
};

/**
 * A generic function that validates a provided boolean parameter
 */
export const validateBooleanParam: ApiParamGenericValidator<boolean> = (
    param,
    options = {}
) => {
    const { optional } = options;

    const isParamUndefined = isUndefined(param);
    const isParamBoolean = typeof param === 'boolean';

    if (optional) {
        return isParamBoolean || isParamUndefined;
    }

    const isMissing = isParamUndefined && !optional;
    const isInvalid = !isParamBoolean;

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided string parameter
 */
export const validateStringParam: ApiParamDetailedValidator<string> = (
    param,
    options = {}
) => {
    const { optional } = options;

    const isParamUndefined = isUndefined(param);
    const isMissing = isParamUndefined && !optional;
    const isValidString = isString(param);
    const isInvalid = optional
        ? !isParamUndefined && !isValidString
        : !isValidString;

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided string parameter as a UUID string
 */
export const validateUUIDParam: ApiParamDetailedValidator<string> = (
    param,
    options = {}
) => {
    const { optional } = options;

    const isMissing = !param && !optional;
    const isInvalid = Boolean(param && !generalUtils.isValidUUID(param));

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided string array parameter as UUID array
 */
export const validateUUIDArrayParam: ApiParamDetailedValidator<string[]> = (
    param,
    options = {}
) => {
    const { optional } = options;

    let isInvalid = false;
    const isMissing = !param && !optional;

    if (param) {
        const isArray = Array.isArray(param);
        const isNotUUIDArray =
            isArray && param.some((value) => !generalUtils.isValidUUID(value));
        isInvalid = !isArray || isNotUUIDArray;
    }

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided string parameter as an ISO 8601 date-time string
 */
export const validateISOParam: ApiParamDetailedValidator<string> = (
    param,
    options = {}
) => {
    const { optional } = options;

    const isMissing = !param && !optional;
    const isInvalid = Boolean(param && !dateUtils.isValidDateISO(param));

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided number as an integer
 */
export const validateIntegerParam: ApiParamDetailedValidator<number> = (
    param,
    options = {}
) => {
    const { optional } = options;

    const isMissing = !param && param !== 0 && !optional;
    const isInvalid = Boolean(param && !isInteger(param));

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided number as a `wise-api` page value
 *
 * Some `wise-api` endpoints support pagination, and the `page` parameter allows for retrieving a specific page
 *
 * Page values are zero-indexed, positive integers
 */
export const validatePageParam: ApiParamDetailedValidator<number> = (
    param,
    options = {}
) => {
    const { isMissing, isInvalid: isInvalidInteger } = validateIntegerParam(
        param,
        options
    );

    const isInvalid = isInvalidInteger || (param as number) < 0;

    return {
        isMissing,
        isInvalid
    };
};

/**
 * A generic function that validates a provided number as a `wise-api` page limit value
 *
 * Some `wise-api` endpoints support pagination, and the `limit` parameter allows for retrieving a set page size
 *
 * Page values are non-zero, positive integers
 */
export const validatePageLimitParam: ApiParamDetailedValidator<number> = (
    param,
    options = {}
) => {
    const { isMissing, isInvalid: isInvalidInteger } = validateIntegerParam(
        param,
        options
    );

    const isInvalid = isInvalidInteger || (param as number) <= 0;

    return {
        isMissing,
        isInvalid
    };
};

/**
 * A generic function that validates a provided date constructor value
 *
 * A valid date constructor can be:
 * + `string`: value must parse into a Date Object
 * + `number`: value must parse into a Date Object
 * + `Date`: value is a javascript Date Object
 *
 * @see [JS Date Object]{@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date}
 */
export const validateDateConstructorParam: ApiParamDetailedValidator<
    Date | string | number
> = (param, options = {}) => {
    const { optional } = options;

    const isJsDateParam = param instanceof Date;
    const isStringParam = typeof param === 'string';
    const isNumberParam = typeof param === 'number';
    const isDateConstructorInput =
        isJsDateParam || isStringParam || isNumberParam;

    const isMissing = !param && param !== 0 && !optional;
    const isInvalid =
        isDateConstructorInput && Number.isNaN(new Date(param).valueOf());

    return { isMissing, isInvalid };
};

/**
 * A generic function that validates a provided string as a UUID string
 *
 * This function can be used as-is to test for a UUID string parameter.
 * Without the `errorMessageMap` parameter, this function will return generic error messages.
 *
 * However, it recommended that you provide more specific error messages to facilitate debugging.
 *
 * @param param the parameter value to test
 * @param errorMessageMap the custom error mapping
 * @returns the error message
 */
export const getParamErrorUUID: StringParamValidationCallback = (
    param,
    errorMessageMap
) => {
    const { missing, invalid } = errorMessageMap || {};

    const errorMessages = {
        missingMessage: missing || ApiParamError.GENERIC_MISSING_PARAM,
        invalidMessage:
            invalid || ApiParamError.GENERIC_INVALID_PARAM_STRING_UUID
    };

    const validatorResult = validateUUIDParam(param);
    const { isInvalid, isMissing } = getErrorTestResults(validatorResult);

    return getParamErrors({ ...errorMessages, isMissing, isInvalid });
};

/**
 * A generic function that validates a provided string as an ISO 8601 date-time string
 *
 * This function can be used as-is to test for a  ISO 8601 date-time string parameter.
 * Without the `errorMessageMap` parameter, this function will return generic error messages.
 *
 * However, it recommended that you provide more specific error messages to facilitate debugging.
 *
 * @param param the parameter value to test
 * @param errorMessageMap the custom error mapping
 * @returns the error message
 */
export const getParamErrorDateISO: StringParamValidationCallback = (
    param,
    errorMessageMap
) => {
    const { missing, invalid } = errorMessageMap || {};

    const errorMessages = {
        missingMessage: missing || ApiParamError.GENERIC_MISSING_PARAM,
        invalidMessage:
            invalid || ApiParamError.GENERIC_INVALID_PARAM_STRING_DATE_ISO
    };

    const validatorResult = validateISOParam(param);
    const { isInvalid, isMissing } = getErrorTestResults(validatorResult);

    return getParamErrors({ ...errorMessages, isMissing, isInvalid });
};

/**
 * A generic function that validates a provided number as an integer
 *
 * This function can be used as-is to test for an integer parameter.
 * Without the `errorMessageMap` parameter, this function will return generic error messages.
 *
 * However, it recommended that you provide more specific error messages to facilitate debugging.
 *
 * @param param the parameter value to test
 * @param errorMessageMap the custom error mapping
 * @returns the error message
 */
export const getParamErrorInteger: NumberParamValidationCallback = (
    param,
    errorMessageMap
) => {
    const { missing, invalid } = errorMessageMap || {};

    const errorMessages = {
        missingMessage: missing || ApiParamError.GENERIC_MISSING_PARAM,
        invalidMessage: invalid || ApiParamError.GENERIC_INVALID_PARAM_INTEGER
    };

    const validatorResult = validateIntegerParam(param);
    const { isInvalid, isMissing } = getErrorTestResults(validatorResult);

    return getParamErrors({ ...errorMessages, isMissing, isInvalid });
};

/**
 * A generic function that validates a provided number as an integer
 *
 * This function can be used as-is to test for an integer parameter.
 * Without the `errorMessageMap` parameter, this function will return generic error messages.
 *
 * However, it recommended that you provide more specific error messages to facilitate debugging.
 *
 * @param param the parameter value to test
 * @param errorMessageMap the custom error mapping
 * @returns the error message
 */
export const getParamErrorAssignmentStatus: AssignmentStatusParamValidationCallback =
    (param, errorMessageMap) => {
        const { missing, invalid } = errorMessageMap || {};

        const validAssignmentStatus = [
            AssignmentStatus.ASSIGNED,
            AssignmentStatus.IN_PROGRESS,
            AssignmentStatus.COMPLETED,
            AssignmentStatus.CANCELED
        ];

        const errorMessages = {
            missingMessage: missing || ApiParamError.MISSING_ASSIGNMENT_STATUS,
            invalidMessage: invalid || ApiParamError.INVALID_ASSIGNMENT_STATUS
        };

        const isMissing = !param && param !== 0;
        const isInvalid = !isMissing && !validAssignmentStatus.includes(param);

        return getParamErrors({ ...errorMessages, isMissing, isInvalid });
    };

/**
 * An implementation of `getParamErrorUUID`
 *
 * This function validates a parameter that represents a `driver ID`.
 *
 * This returns error messages that identify the parameter as `driver ID`
 *
 * @param param the parameter value to test
 * @returns the error message
 */
export const getParamErrorDriverId: StringParamValidationCallback = (param) => {
    const errorMessageMap = {
        missing: ApiParamError.MISSING_DRIVER_ID,
        invalid: ApiParamError.INVALID_DRIVER_ID
    };
    return getParamErrorUUID(param, errorMessageMap);
};

/**
 * An implementation of `getParamErrorUUID`
 *
 * This function validates a parameter that represents an `assignment ID`.
 *
 * This returns error messages that identify the parameter as `assignment ID`
 *
 * @param param the parameter value to test
 * @returns the error message
 */
export const getParamErrorAssignmentId: StringParamValidationCallback = (
    param
) => {
    const errorMessageMap = {
        missing: ApiParamError.MISSING_ASSIGNMENT_ID,
        invalid: ApiParamError.INVALID_ASSIGNMENT_ID
    };
    return getParamErrorUUID(param, errorMessageMap);
};

/**
 * An implementation of `getParamErrorDateISO`
 *
 * This function validates a parameter that represents `timestamp`.
 *
 * This returns error messages that identify the parameter as `timestamp`
 *
 * @param param the parameter value to test
 * @returns the error message
 */
export const getParamErrorTimestamp: StringParamValidationCallback = (
    param
) => {
    const errorMessageMap = {
        missing: ApiParamError.MISSING_TIMESTAMP,
        invalid: ApiParamError.INVALID_TIMESTAMP
    };
    return getParamErrorDateISO(param, errorMessageMap);
};

/**
 * An implementation of `getParamErrorTaskWindowSlots`
 *
 * This function validates a parameter that represents `delivery/pickup windows`.
 *
 * This returns error messages that identify the parameter as `delivery/pickup windows`
 *
 * @param param the parameter value to test
 * @returns the error message
 */
export const getParamErrorTaskWindowSlots: TaskDataParamValidationCallback = (
    param
) => {
    const { props } = param || {};

    const dataDelivery = (props as DeliveryTaskProps)?.deliveryWindow;
    if (dataDelivery) {
        const filteredDelivery = dataDelivery.filter(
            (info: { start: string; end: string }) => {
                const startDateTime = new Date(info.start);
                const endDateTime = new Date(info.end);
                return startDateTime > endDateTime;
            }
        );
        if (filteredDelivery.length) {
            return ApiParamError.INVALID_DELIVERY_WINDOW;
        }
    }

    const dataPickup = (props as PickupTaskProps)?.pickupWindow;
    if (dataPickup) {
        const filteredPickup = dataPickup.filter(
            (info: { start: string; end: string }) => {
                const startDateTime = new Date(info.start);
                const endDateTime = new Date(info.end);
                return startDateTime > endDateTime;
            }
        );
        if (filteredPickup.length) {
            return ApiParamError.INVALID_PICKUP_WINDOW;
        }
    }
    return undefined;
};

/**
 * A generic function to validate all API parameters against a given mapping of validators
 *
 * The `validatorMap` requires at least the `validator`, which can be any of the folloving callback functions:
 * 1. validator returns a boolean
 *    + returns `true` when `param` is valid
 *    + returns `false` when `param` is not valid
 * 2. validator returns a validation object
 *    + tests `isMissing` when `param` is missing and returns result
 *    + tests `isInvalid` when `param` is not valid and returns result
 *
 * The results from any validator type will be standarized to return the expected error messages
 *
 * @param params The API parameters to test
 * @param validatorMap The validation map
 */
const getParamErrorsWithValidationMap = <T extends { [K: string]: unknown }>(
    params: T,
    validatorMap: ApiParamValidatorMap<T>
) => {
    try {
        const allParams = [
            ...new Set([...Object.keys(params), ...Object.keys(validatorMap)])
        ];

        const paramsErrors = allParams.reduce(
            (allErrors, paramKey) => {
                const {
                    validator,
                    optional,
                    errorMessage,
                    missingMessage,
                    invalidMessage
                } = validatorMap[paramKey] || {};

                if (!validator) {
                    const unknownError = `${ApiParamError.GENERIC_MISSING_VALIDATOR} (${paramKey})`;
                    allErrors.unknownErrors.push(unknownError);

                    return allErrors;
                }

                const paramValue = params[paramKey] as Exclude<
                    T[typeof paramKey],
                    undefined
                >;

                const validatorResult = validator(paramValue, { optional });

                const { isInvalid, isMissing } =
                    getErrorTestResults(validatorResult);

                const knownError = getParamErrors({
                    isMissing,
                    isInvalid,
                    errorMessage,
                    missingMessage,
                    invalidMessage
                });

                if (knownError) {
                    allErrors.knownErrors.push(knownError);
                }

                return allErrors;
            },
            {
                knownErrors: [],
                unknownErrors: []
            } as ApiParamErrorMap
        );

        return paramsErrors;
    } catch (e) {
        return {
            knownErrors: [ApiParamError.GENERIC_VALIDATOR_MAP_ERROR],
            unknownErrors: []
        };
    }
};

/**
 * Validates all API parameters against the provided validator map
 *
 * The `validatorMap` requires at least the `validator`, which can be any of the following callback functions:
 * 1. validator returns a boolean
 *    + returns `true` when `param` is valid
 *    + returns `false` when `param` is not valid
 * 2. validator returns a validation object
 *    + tests `isMissing` when `param` is missing and returns result
 *    + tests `isInvalid` when `param` is not valid and returns result
 *
 * The `validator` callback will test the parameter as if it was typed as `Required<T>`
 *
 * Optional parameters must explicity set `optional: true` in order to accept `undefined` as a valid non-missing value
 */
export const validateAllParams = <T extends { [K: string]: unknown }>(
    params: T,
    validatorMap: ApiParamValidatorMap<T>
) => {
    const { knownErrors, unknownErrors } = getParamErrorsWithValidationMap(
        params,
        validatorMap
    );

    const compiledErrors = [...knownErrors, ...unknownErrors].join('; ');

    return compiledErrors || undefined;
};

/**
 * Validates only known API parameters against the provided validator map
 *
 * Known API parameters are parameters with a configured validator from the validator map
 *
 * Unknown API parameters will be ignored for the purposes of validation
 *
 * The `validatorMap` requires at least the `validator`, which can be any of the folloving callback functions:
 * 1. validator returns a boolean
 *    + returns `true` when `param` is valid
 *    + returns `false` when `param` is not valid
 * 2. validator returns a validation object
 *    + tests `isMissing` when `param` is missing and returns result
 *    + tests `isInvalid` when `param` is not valid and returns result
 *
 * The `validator` callback will test the parameter as if it was typed as `Required<T>`
 *
 * Optional parameters must explicity set `optional: true` in order to accept `undefined` as a valid non-missing value
 */
export const validateOnlyKnownParams = <T extends { [K: string]: unknown }>(
    params: T,
    validatorMap: ApiParamValidatorMap<T>
) => {
    const { knownErrors } = getParamErrorsWithValidationMap(
        params,
        validatorMap
    );
    const compiledErrors = knownErrors.join('; ');
    return compiledErrors || undefined;
};

export interface ValidationFailure {
    type: 'VALIDATION_ERROR';
    errors: Record<string, string>;
}

export const validateApiRequest = async <T>(
    schema: AnySchema<T>,
    data: unknown
): Promise<T> => {
    try {
        // Returns T (the inferred type from the schema)
        return (await schema.validate(data, {
            abortEarly: false,
            stripUnknown: true
        })) as T;
    } catch (err) {
        if (err instanceof ValidationError) {
            // formik utility flattens the yup errors into an object keyed by params
            const paramErrors = yupToFormErrors(err);
            const flatFormErrors = Object.entries(paramErrors).reduce<
                Record<string, unknown>
            >((acc, [param, error]) => {
                if (Array.isArray(error)) {
                    error.forEach((paramItemError, paramItemErrorIndex) => {
                        const paramItemPath = `${param}[${paramItemErrorIndex}]`;
                        acc[paramItemPath] = paramItemError;
                    });
                    return acc;
                }

                acc[param] = error;
                return acc;
            }, {});

            return Promise.reject({
                type: 'VALIDATION_ERROR',
                errors: flatFormErrors
            } as ValidationFailure);
        }
        throw err;
    }
};
